/**
 * The made-to-measure events (Villa Dior), sorted in the browser, edited or
 * archived. Admin only.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBoxArchive,
  faCircleNotch,
  faPencilAlt,
} from '@fortawesome/free-solid-svg-icons';
import { MTM_ROUTE_PATH, PLATFORM, ROLES } from '@/shared/constants';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { snackbar } from '@/shared/ui/snackbar';
import { useSavedDialogClose } from '../use-referential';
import plusIcon from '@/assets/images/plus.svg';
import { nextListSort } from '../list';
import {
  archiveDmsEvent,
  dmsEventsQueryKey,
  dmsEventsQueryOptions,
  type DmsEvent,
} from './api';
import {
  eventPictureUrl,
  formatEventDate,
  sortedEvents,
  type EventSort,
  type EventSortField,
} from './event-form';
import { EventDialog } from './EventDialog';
import { kebabCase } from '@/shared/lib/kebab-case';

const COLUMNS: { field: EventSortField; label: string; width: string }[] = [
  { field: 'name', label: 'Name', width: '16%' },
  { field: 'type', label: 'Type', width: '12%' },
  { field: 'location', label: 'Location', width: '14%' },
  { field: 'storeCode', label: 'Store Code', width: '10%' },
  { field: 'eventId', label: 'Event Id', width: '10%' },
  { field: 'startDate', label: 'Start Date', width: '10%' },
  { field: 'endDate', label: 'End Date', width: '10%' },
];

export function EventsPage() {
  const user = getUserProfile();
  const isAdmin = user?.role === ROLES.ADMIN;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !isAdmin) hardNavigate(`/${MTM_ROUTE_PATH}/main`);
  }, [user, isAdmin]);

  const events = useQuery({ ...dmsEventsQueryOptions(), enabled: isAdmin });
  useEffect(() => {
    if (events.isError)
      snackbar(
        'Unexpected error while loading the events, please try again later',
      );
  }, [events.isError]);

  const [sort, setSort] = useState<EventSort>({
    by: 'startDate',
    reverse: false,
  });
  const [dialog, setDialog] = useState<{ item: DmsEvent | null } | null>(null);
  const [archiving, setArchiving] = useState<DmsEvent | null>(null);

  const items = useMemo(() => events.data ?? [], [events.data]);
  const displayed = sortedEvents(items, sort);
  const isLoading = events.isFetching;

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: dmsEventsQueryKey });

  const archive = useMutation({
    mutationFn: (item: DmsEvent) => archiveDmsEvent(item.id),
    onSuccess: () => {
      snackbar('Successfully archived');
      void refresh();
    },
    onError: () => snackbar('Failed to archive event, please try again later'),
  });

  const closeDialog = useSavedDialogClose(() => setDialog(null), refresh);

  if (!isAdmin) return null;

  return (
    <div className="container summary">
      <TopMenu platform={PLATFORM.DMS} />
      <div className="summary-table mb-12 mt-4">
        <div className="relative mb-4 flex items-end justify-between">
          <div>
            <h2>Events</h2>
          </div>
          <button
            type="button"
            className="add-btn absolute-x-center flex items-center"
            data-testid="header-add-event"
            onClick={() => {
              if (!isLoading) setDialog({ item: null });
            }}
          >
            <img src={plusIcon} alt="" />
            <span className="pl-2">Add a new event</span>
          </button>
          <div />
        </div>
        <div className="table-container">
          <div className="items-container" role="region" aria-label="Events">
            <table
              className="table items"
              style={{
                display: 'block',
                overflowX: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: '8%' }}>Picture</th>
                  {COLUMNS.map(({ field, label, width }) => (
                    <SortableHeader
                      key={field}
                      label={label}
                      width={width}
                      testId={`mtm-event-header-${kebabCase(field)}`}
                      active={sort.by === field}
                      descending={sort.reverse}
                      onClick={() =>
                        setSort((current) => nextListSort(current, field))
                      }
                    />
                  ))}
                  <th style={{ width: '10%' }} className="edit-header">
                    actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayed.map((item) => {
                  const picture = eventPictureUrl(item);
                  return (
                    <tr key={item.id} className="user-row">
                      <td
                        style={{ width: '8%' }}
                        data-testid="mtm-event-row-picture"
                      >
                        {picture && (
                          <img
                            src={picture}
                            className="event-thumbnail"
                            alt=""
                          />
                        )}
                      </td>
                      <td
                        style={{ width: '16%' }}
                        title={item.name ?? ''}
                        data-testid="mtm-event-row-name"
                      >
                        {item.name}
                      </td>
                      <td
                        style={{ width: '12%' }}
                        title={item.type ?? ''}
                        data-testid="mtm-event-row-type"
                      >
                        {item.type}
                      </td>
                      <td
                        style={{ width: '14%' }}
                        title={item.location ?? ''}
                        data-testid="mtm-event-row-location"
                      >
                        {item.location}
                      </td>
                      <td
                        style={{ width: '10%' }}
                        title={item.storeCode ?? ''}
                        data-testid="mtm-event-row-store-code"
                      >
                        {item.storeCode}
                      </td>
                      <td
                        style={{ width: '10%' }}
                        title={item.eventId ?? ''}
                        data-testid="mtm-event-row-event-id"
                      >
                        {item.eventId}
                      </td>
                      <td
                        style={{ width: '10%' }}
                        data-testid="mtm-event-row-start-date"
                      >
                        {formatEventDate(item.startDate)}
                      </td>
                      <td
                        style={{ width: '10%' }}
                        data-testid="mtm-event-row-end-date"
                      >
                        {formatEventDate(item.endDate)}
                      </td>
                      <td style={{ width: '10%' }} className="edit-action">
                        <button
                          type="button"
                          className="action-icon"
                          title="Edit"
                          aria-label={`Edit ${item.name ?? ''}`}
                          data-testid="mtm-event-row-edit-button"
                          onClick={() => {
                            if (!isLoading) setDialog({ item });
                          }}
                        >
                          <FontAwesomeIcon icon={faPencilAlt} />
                        </button>
                        <button
                          type="button"
                          className="action-icon ml-2"
                          title="Archive"
                          aria-label={`Archive ${item.name ?? ''}`}
                          data-testid="mtm-event-row-archive-button"
                          onClick={() => {
                            if (!isLoading) setArchiving(item);
                          }}
                        >
                          <FontAwesomeIcon icon={faBoxArchive} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {isLoading && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          className="loading-icon"
          aria-label="Loading"
        />
      )}
      {dialog && (
        <EventDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          onClose={closeDialog}
        />
      )}
      {archiving !== null && (
        <ConfirmDialog
          title="Archive this event?"
          text="The event will be moved to the archives and removed from the active events."
          onConfirm={() => {
            if (archiving) archive.mutate(archiving);
            setArchiving(null);
          }}
          onCancel={() => setArchiving(null)}
        />
      )}
    </div>
  );
}
